import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash, FloppyDisk, Queue, Phone, TreeStructure, Users, Robot, ShieldCheck, X, CaretDown, Sparkle, PaperPlaneRight, CheckCircle, Timer, Gauge, LockKey } from "@phosphor-icons/react";

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const DEFAULT_AI = { greeting: "", voice: "joanna", verify: true, verify_method: "dob", verify_field: "", verify_label: "", escalate_on_confused: true, objects: ["Order", "Case"], custom_objects: [], object_prompts: {}, verify_fail_message: "", enable_actions: true, can_create_case: true, can_create_event: true, can_create_task: true, role: "", tone: "", goal: "", instructions: "" };

// Fetches an SObject's fields from Salesforce for a picker; falls back to a plain
// text input when SF metadata can't be loaded (e.g. not connected).
function SfFieldPicker({ object, value, onChange, testId, placeholder, numericOnly }) {
    const [fields, setFields] = useState(null); // null=loading, []=unavailable
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/integrations/salesforce/describe/${object}`, { params: { all: 1 } });
                let fs = data.fields || [];
                if (numericOnly) {
                    const ok = ["int", "double", "currency", "percent", "phone", "string", "textarea", "id"];
                    fs = fs.filter((f) => ok.includes((f.type || "").toLowerCase()));
                }
                if (alive) setFields(fs);
            } catch (_e) { if (alive) setFields([]); }
        })();
        return () => { alive = false; };
    }, [object, numericOnly]);

    if (fields === null) {
        return <div data-testid={`${testId}-loading`} className="mt-1.5 h-9 w-full animate-pulse rounded-lg bg-stone-100" />;
    }
    if (fields.length === 0) {
        return (
            <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
        );
    }
    const known = fields.some((f) => f.name === value);
    return (
        <select data-testid={testId} value={known ? value : ""} onChange={(e) => onChange(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink">
            <option value="">— Select a field —</option>
            {fields.map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
            {value && !known && <option value={value}>{value}</option>}
        </select>
    );
}

// Custom-object picker backed by the SObjects list, with manual entry fallback.
function SfCustomObjectAdder({ existing, onAdd, testPrefix }) {
    const [objs, setObjs] = useState(null);
    const [manual, setManual] = useState("");
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/integrations/salesforce/sobjects", { params: { custom: 1 } });
                if (alive) setObjs(data.sobjects || []);
            } catch (_e) { if (alive) setObjs([]); }
        })();
        return () => { alive = false; };
    }, []);
    const has = (v) => existing.some((o) => o.toLowerCase() === v.toLowerCase());
    if (objs && objs.length > 0) {
        const avail = objs.filter((o) => !has(o.name));
        return (
            <div className="flex items-center gap-2">
                <select data-testid={`${testPrefix}-custom-select`} value="" onChange={(e) => { if (e.target.value) onAdd(e.target.value); }}
                    className="w-52 rounded-lg border border-stone-300 bg-white px-2 py-1 text-xs outline-none focus:border-ink">
                    <option value="">+ Add custom object…</option>
                    {avail.map((o) => <option key={o.name} value={o.name}>{o.label} ({o.name})</option>)}
                </select>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2">
            <input data-testid={`${testPrefix}-custom-input`} value={manual} onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (manual.trim()) { onAdd(manual.trim()); setManual(""); } } }}
                placeholder="e.g. Policy__c"
                className="w-36 rounded-lg border border-dashed border-stone-300 bg-white px-2.5 py-1 text-xs outline-none focus:border-ink" />
            <button data-testid={`${testPrefix}-custom-add`} onClick={() => { if (manual.trim()) { onAdd(manual.trim()); setManual(""); } }}
                className="rounded-full border border-stone-300 px-2.5 py-1 text-xs font-semibold text-ink hover:bg-white">Add</button>
        </div>
    );
}
const AI_READ_OBJECTS = ["Order", "Case", "Task", "Event"];
const AI_ROLES = ["Sales Assistant", "Support Assistant", "Appointment Assistant", "Billing Assistant", "Custom"];
const AI_TONES = ["Professional", "Friendly", "Formal", "Sales Focused"];
const AI_VOICE_OPTIONS = [
    { key: "joanna", label: "Joanna — US English · female (professional)" },
    { key: "matthew", label: "Matthew — US English · male (professional)" },
    { key: "amy", label: "Amy — British English · female" },
    { key: "kajal", label: "Kajal — Indian English · female" },
];
const AI_GOALS = ["Resolve Customer Queries", "Generate Leads", "Book Appointments", "Upsell Products", "Reduce Agent Transfers"];

function AgentChips({ agents, selected, onToggle, testPrefix }) {
    return (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={testPrefix}>
            {agents.length === 0 && <span className="text-xs text-stone2">No team members yet.</span>}
            {agents.map((a) => {
                const on = selected.includes(a.id);
                return (
                    <button
                        key={a.id}
                        data-testid={`${testPrefix}-agent-${a.id}`}
                        onClick={() => onToggle(a.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                            on ? "border-ink bg-ink text-white" : "border-stone-300 bg-white text-stone2 hover:border-ink"
                        }`}
                    >
                        {a.name || a.email}
                    </button>
                );
            })}
        </div>
    );
}

function RouteSelector({ value, onChange, allowSubmenu, testPrefix, aiLocked }) {
    const opts = [
        { key: "agents", label: "Agents", icon: Users },
        ...(allowSubmenu ? [{ key: "submenu", label: "Sub-menu", icon: TreeStructure }] : []),
        { key: "ai_assistant", label: "AI Assistant", icon: Robot, locked: aiLocked },
    ];
    return (
        <div className="mt-3">
            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Route to</label>
            <div className="mt-1.5 inline-flex rounded-full border border-stone-300 bg-white p-1">
                {opts.map((o) => {
                    const Icon = o.icon;
                    const on = value === o.key;
                    if (o.locked) {
                        return (
                            <span
                                key={o.key}
                                data-testid={`${testPrefix}-${o.key}-locked`}
                                title="Upgrade to Professional to unlock the AI Voice Assistant"
                                className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-stone-400"
                            >
                                <LockKey size={13} weight="fill" /> {o.label}
                                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-bold uppercase text-amber-700">Pro</span>
                            </span>
                        );
                    }
                    return (
                        <button
                            key={o.key}
                            data-testid={`${testPrefix}-${o.key}`}
                            onClick={() => onChange(o.key)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                                on ? "bg-ink text-white" : "text-stone2 hover:text-ink"
                            }`}
                        >
                            <Icon size={14} weight={on ? "fill" : "regular"} /> {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function AiTestModal({ cfg, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("Where is my order?");
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [err, setErr] = useState("");

    const send = async () => {
        const text = input.trim();
        if (!text || loading) return;
        const next = [...messages, { role: "user", text }];
        setMessages(next);
        setInput("");
        setLoading(true);
        setErr("");
        try {
            const { data } = await api.post("/ai/test", { ai_config: cfg, messages: next });
            if (data.error) {
                const budget = /budget/i.test(data.error);
                setErr(budget ? "AI model unavailable — add balance to your Universal Key (Profile → Universal Key → Add Balance) to enable live replies." : data.error);
            } else {
                setMessages([...next, { role: "assistant", text: data.reply }]);
                setMeta({ objects_read: data.objects_read || [], actions: data.actions || [], confidence: data.confidence, elapsed_ms: data.elapsed_ms });
            }
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    const objs = meta?.objects_read || cfg.objects || [];
    return (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-ink/40 p-4" data-testid="ai-test-modal" onClick={onClose}>
            <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-600 text-white"><Sparkle size={16} weight="fill" /></span>
                        <div>
                            <div className="font-heading text-base font-bold">AI Assistant Playground</div>
                            <div className="text-[11px] text-stone2">Sandbox — never touches your live Salesforce data.</div>
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="ai-test-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100"><X size={18} /></button>
                </div>
                <div className="grid flex-1 grid-cols-1 overflow-hidden md:grid-cols-5">
                    <div className="flex flex-col border-stone-200 md:col-span-3 md:border-r">
                        <div className="max-h-[52vh] min-h-[280px] flex-1 space-y-2 overflow-y-auto p-4" data-testid="ai-test-conversation">
                            {messages.length === 0 && <div className="grid h-full place-items-center px-6 text-center text-sm text-stone2">Type a customer question below to preview how your assistant responds.</div>}
                            {messages.map((m, i) => (
                                <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                                    <span data-testid={m.role === "assistant" ? "ai-test-reply" : undefined} className={`inline-block max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-ink text-white" : "border border-stone-200 bg-stone-50 text-ink"}`}>{m.text}</span>
                                </div>
                            ))}
                            {loading && <div className="text-left"><span className="inline-block rounded-2xl border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone2">Thinking…</span></div>}
                        </div>
                        {err && <div className="mx-4 mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600" data-testid="ai-test-error">{err}</div>}
                        <div className="flex items-center gap-2 border-t border-stone-200 p-3">
                            <input data-testid="ai-test-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="e.g. Where is my order?" className="flex-1 rounded-full border border-stone-300 px-4 py-2 text-sm outline-none focus:border-ink" />
                            <button data-testid="ai-test-send" onClick={send} disabled={loading} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><PaperPlaneRight size={15} weight="fill" /> Send</button>
                        </div>
                    </div>
                    <div className="space-y-4 bg-stone-50/60 p-4 md:col-span-2">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Salesforce Objects Read</div>
                            <div className="mt-2 space-y-1.5" data-testid="ai-test-objects">
                                {objs.length ? objs.map((o) => (
                                    <div key={o} className="flex items-center gap-2 text-sm text-ink"><CheckCircle size={15} weight="fill" className="text-emerald-500" /> {o}</div>
                                )) : <div className="text-sm text-stone2">None</div>}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Actions</div>
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-test-actions">
                                {meta?.actions?.length ? meta.actions.map((a, i) => <span key={i} className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-medium text-sky-700">{a}</span>) : <span className="text-sm text-stone2">None</span>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl border border-stone-200 bg-white p-3">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone2"><Gauge size={12} weight="fill" /> Confidence</div>
                                <div className="mt-1 font-heading text-2xl font-bold tabular" data-testid="ai-test-confidence">{meta?.confidence != null ? `${meta.confidence}%` : "—"}</div>
                            </div>
                            <div className="rounded-xl border border-stone-200 bg-white p-3">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone2"><Timer size={12} weight="fill" /> Time</div>
                                <div className="mt-1 font-heading text-2xl font-bold tabular" data-testid="ai-test-time">{meta?.elapsed_ms != null ? `${(meta.elapsed_ms / 1000).toFixed(1)}s` : "—"}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AiToggle({ testId, checked, onChange }) {
    return (
        <button type="button" role="switch" aria-checked={!!checked} data-testid={testId} onClick={() => onChange(!checked)}
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-stone-300"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
        </button>
    );
}

function AiSection({ step, title, subtitle, icon: Icon, children, testId }) {
    return (
        <div className="rounded-xl border border-indigo-200 bg-white" data-testid={testId}>
            <div className="flex items-start gap-2.5 border-b border-indigo-100 px-3 py-2.5">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">{step}</span>
                <div>
                    <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-800">{Icon && <Icon size={13} weight="fill" />} {title}</div>
                    {subtitle && <p className="mt-0.5 text-[11px] leading-snug text-stone2">{subtitle}</p>}
                </div>
            </div>
            <div className="space-y-3 px-3 py-3">{children}</div>
        </div>
    );
}

function AiScriptPreview({ c, stdReads, customReads }) {
    const reads = [...stdReads, ...customReads];
    const creates = [];
    if (c.can_create_case !== false) creates.push("open a support case");
    if (c.can_create_event !== false) creates.push("book an appointment");
    if (c.can_create_task !== false) creates.push("log a callback");
    const actionsOn = c.enable_actions !== false && creates.length > 0;
    const vmethod = c.verify_method || "dob";
    const vlabel = vmethod === "field" ? ((c.verify_label || "").trim() || "verification detail") : "date of birth";
    const persona = [c.role, c.tone].filter(Boolean).join(", ").toLowerCase();

    const Step = ({ n, title, children, tone = "indigo" }) => (
        <li className="flex gap-2.5">
            <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white ${tone === "amber" ? "bg-amber-500" : "bg-indigo-600"}`}>{n}</span>
            <div className="text-[12px] leading-relaxed text-ink">
                <span className="font-bold text-indigo-800">{title}</span> — {children}
            </div>
        </li>
    );

    return (
        <div className="rounded-xl border border-indigo-200 bg-white/70 p-3" data-testid="ai-script-preview-body">
            <p className="mb-2 text-[11px] text-stone2">This is exactly what your assistant does on every call, in order:</p>
            <ol className="space-y-2.5">
                <Step n="1" title="Greeting">
                    {(c.greeting || "").trim()
                        ? <>it opens with your message: <i>“{c.greeting.trim()}”</i></>
                        : <>it opens with an auto-generated hello (uses the caller's name when known).</>}
                    {persona && <> Speaks as a {persona} assistant.</>}
                </Step>
                {c.verify ? (
                    <Step n="2" title="Verifies identity first">
                        before anything else it asks the caller to enter their <b>{vlabel}</b> on the keypad. If the number isn't found in Salesforce (or has no {vlabel} on file) it connects a live agent; two wrong tries plays {(c.verify_fail_message || "").trim() ? <>your message <i>“{c.verify_fail_message.trim()}”</i></> : "your ‘can’t verify’ message"} and ends the call.
                    </Step>
                ) : (
                    <Step n="2" title="Verification is OFF" tone="amber">
                        the assistant helps without confirming who the caller is. Turn verification on above for sensitive accounts.
                    </Step>
                )}
                <Step n="3" title="Looks things up">
                    {reads.length > 0
                        ? <>once verified it can answer questions about: <b>{reads.join(", ")}</b>.</>
                        : <>no record types are selected, so it won't look anything up.</>}
                </Step>
                <Step n="4" title="Takes actions">
                    {actionsOn
                        ? <>it can <b>{creates.join(", ")}</b> — it reads the details back and gets a “yes” first, and never creates the same thing twice on one call. Emails a confirmation when the contact has an email on file.</>
                        : <>it won't create any records (creation is off).</>}
                </Step>
                <Step n="5" title="Wraps up">
                    {c.escalate_on_confused
                        ? "hands off to a live agent on request, complaint, or repeated confusion."
                        : "hands off to an agent only when the caller asks for a human."} At the end it logs a call summary and any created records to Salesforce, and the call shows an “Open in Salesforce” link in Call History.
                </Step>
            </ol>
        </div>
    );
}

function AiConfigEditor({ cfg, onChange, testPrefix }) {
    const c = { ...DEFAULT_AI, ...(cfg || {}) };
    const [customInput, setCustomInput] = useState("");
    const [behaviourOpen, setBehaviourOpen] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(true);

    // Standard reads are locked to Order/Case/Task/Event. Migrate any legacy custom
    // entries that were stored in `objects` into the custom (search-only) list.
    const stdSet = AI_READ_OBJECTS.map((s) => s.toLowerCase());
    const stdReads = (c.objects || []).map((o) => AI_READ_OBJECTS.find((s) => s.toLowerCase() === (o || "").toLowerCase())).filter(Boolean);
    let customReads = c.custom_objects || [];
    if (customReads.length === 0) {
        const legacy = (c.objects || []).filter((o) => !stdSet.includes((o || "").toLowerCase()));
        if (legacy.length) customReads = legacy;
    }
    const toggleRead = (obj) => {
        const has = stdReads.some((o) => o.toLowerCase() === obj.toLowerCase());
        onChange({ objects: has ? stdReads.filter((o) => o.toLowerCase() !== obj.toLowerCase()) : [...stdReads, obj] });
    };
    const addCustom = () => {
        const v = customInput.trim();
        if (!v) return;
        if (!customReads.some((o) => o.toLowerCase() === v.toLowerCase())) onChange({ custom_objects: [...customReads, v] });
        setCustomInput("");
    };

    return (
        <div className="mt-3 space-y-3 rounded-xl border-l-2 border-indigo-300 bg-indigo-50/40 p-3" data-testid={testPrefix}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs font-bold text-indigo-700">
                    <Robot size={15} weight="fill" /> AI voice assistant · Salesforce
                </div>
                <button type="button" data-testid={`${testPrefix}-test-btn`} onClick={() => setTestOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5">
                    <Sparkle size={13} weight="fill" /> Test
                </button>
            </div>
            {testOpen && <AiTestModal cfg={c} onClose={() => setTestOpen(false)} />}

            {/* Plain-English preview of the exact scripted call flow (updates live) */}
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/60" data-testid={`${testPrefix}-script-preview`}>
                <button type="button" data-testid={`${testPrefix}-script-toggle`} onClick={() => setPreviewOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-3 py-2.5 text-left">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-800"><PaperPlaneRight size={13} weight="fill" /> Call flow preview</span>
                    <CaretDown size={14} className={`text-indigo-400 transition-transform ${previewOpen ? "rotate-180" : ""}`} />
                </button>
                {previewOpen && <div className="px-3 pb-3"><AiScriptPreview c={c} stdReads={stdReads} customReads={customReads} /></div>}
            </div>

            {/* 1 · Behaviour */}
            <AiSection step="1" title="Behaviour" subtitle="How the assistant sounds and what it focuses on." icon={Robot} testId={`${testPrefix}-sec-behaviour`}>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Greeting (optional — auto-generated if blank)</label>
                    <textarea data-testid={`${testPrefix}-greeting`} value={c.greeting || ""} onChange={(e) => onChange({ greeting: e.target.value })} rows={2}
                        placeholder="Hi! I'm the Acme virtual assistant. How can I help with your order today?"
                        className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Voice</label>
                    <select data-testid={`${testPrefix}-voice`} value={c.voice || "joanna"} onChange={(e) => onChange({ voice: e.target.value })}
                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink">
                        {AI_VOICE_OPTIONS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                    </select>
                    <p className="mt-1 text-[10px] text-stone2">Natural, professional text-to-speech voice your callers will hear.</p>
                </div>
                <button type="button" data-testid={`${testPrefix}-behaviour-toggle`} onClick={() => setBehaviourOpen((o) => !o)}
                    className="flex w-full items-center justify-between rounded-lg bg-indigo-50 px-3 py-2 text-left">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-700">Fine-tune role, tone & instructions</span>
                    <CaretDown size={14} className={`text-indigo-400 transition-transform ${behaviourOpen ? "rotate-180" : ""}`} />
                </button>
                {behaviourOpen && (
                    <div className="space-y-3" data-testid={`${testPrefix}-behaviour`}>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Role</label>
                                <select data-testid={`${testPrefix}-role`} value={c.role || ""} onChange={(e) => onChange({ role: e.target.value })}
                                    className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink">
                                    <option value="">— Select —</option>
                                    {AI_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Tone</label>
                                <select data-testid={`${testPrefix}-tone`} value={c.tone || ""} onChange={(e) => onChange({ tone: e.target.value })}
                                    className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink">
                                    <option value="">— Select —</option>
                                    {AI_TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Goal</label>
                                <select data-testid={`${testPrefix}-goal`} value={c.goal || ""} onChange={(e) => onChange({ goal: e.target.value })}
                                    className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-2 py-2 text-sm outline-none focus:border-ink">
                                    <option value="">— Select —</option>
                                    {AI_GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Additional Instructions</label>
                            <textarea data-testid={`${testPrefix}-instructions`} value={c.instructions || ""} onChange={(e) => onChange({ instructions: e.target.value })} rows={3}
                                placeholder="e.g. Always mention our summer promotion. Never promise refunds over $100."
                                className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                        </div>
                    </div>
                )}
            </AiSection>

            {/* 2 · Verification */}
            <AiSection step="2" title="Identity verification" subtitle="The caller must verify before the assistant reads or creates anything." icon={ShieldCheck} testId={`${testPrefix}-sec-verify`}>
                <label className="flex cursor-pointer items-center justify-between gap-3">
                    <span className="text-xs font-medium text-ink">Require identity verification</span>
                    <AiToggle testId={`${testPrefix}-verify`} checked={c.verify} onChange={(v) => onChange({ verify: v })} />
                </label>
                {c.verify && (
                    <>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Verify by (keypad entry)</label>
                            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid={`${testPrefix}-verify-method`}>
                                {[["dob", "Date of birth"], ["field", "A Contact field"]].map(([val, label]) => (
                                    <button key={val} type="button" data-testid={`${testPrefix}-verify-${val}`} onClick={() => onChange({ verify_method: val })}
                                        className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${(c.verify_method || "dob") === val ? "border-indigo-500 bg-indigo-600 text-white" : "border-stone-300 bg-white text-stone2 hover:border-ink"}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {(c.verify_method || "dob") === "field" && (
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Contact field to match</label>
                                    <SfFieldPicker object="Contact" numericOnly value={c.verify_field || ""}
                                        onChange={(v) => onChange({ verify_field: v })}
                                        testId={`${testPrefix}-verify-field`} placeholder="e.g. Account_Number__c" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Spoken name for it</label>
                                    <input data-testid={`${testPrefix}-verify-label`} value={c.verify_label || ""} onChange={(e) => onChange({ verify_label: e.target.value })}
                                        placeholder="e.g. account number"
                                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                </div>
                                <p className="text-[11px] text-stone2 sm:col-span-2">Must be a <b>numeric</b> field on the Contact (account number, PIN, postal code…). The caller enters it on the keypad and it's matched against this field.</p>
                            </div>
                        )}
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Message when we can't verify</label>
                            <input data-testid={`${testPrefix}-verify-fail`} value={c.verify_fail_message || ""} onChange={(e) => onChange({ verify_fail_message: e.target.value })}
                                placeholder="We're sorry, we couldn't verify your identity. Please call us back later."
                                className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                            <p className="mt-1 text-[11px] text-stone2">Played if the caller isn't found, has no value on file, or enters it wrong twice — then the call ends.</p>
                        </div>
                    </>
                )}
            </AiSection>

            {/* 3 · Can READ */}
            <AiSection step="3" title="What it can read" subtitle="Look up the caller's records after they're verified." icon={Queue} testId={`${testPrefix}-sec-read`}>
                <div className="grid grid-cols-2 gap-2" data-testid={`${testPrefix}-read-objects`}>
                    {AI_READ_OBJECTS.map((obj) => {
                        const on = stdReads.some((o) => o.toLowerCase() === obj.toLowerCase());
                        return (
                            <button key={obj} type="button" data-testid={`${testPrefix}-read-${obj}`} onClick={() => toggleRead(obj)}
                                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${on ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-stone-300 bg-white text-stone2 hover:border-ink"}`}>
                                {obj}
                                {on && <CheckCircle size={14} weight="fill" className="text-indigo-500" />}
                            </button>
                        );
                    })}
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Custom objects (search only)</label>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-custom-objects`}>
                        {customReads.map((o) => (
                            <span key={o} className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-700">
                                {o}
                                <button data-testid={`${testPrefix}-custom-remove-${o}`} onClick={() => onChange({ custom_objects: customReads.filter((x) => x !== o) })} className="text-indigo-400 hover:text-red-500">
                                    <X size={12} weight="bold" />
                                </button>
                            </span>
                        ))}
                        <SfCustomObjectAdder existing={customReads} testPrefix={testPrefix}
                            onAdd={(v) => { if (!customReads.some((o) => o.toLowerCase() === v.toLowerCase())) onChange({ custom_objects: [...customReads, v] }); setCustomInput(""); }} />
                    </div>
                    <p className="mt-1 text-[11px] text-stone2">Pick or type a custom object's API name. The assistant can only <b>search</b> its records (matched by ContactId / AccountId) — never create them.</p>
                </div>
                {(stdReads.length + customReads.length) > 0 && (
                    <div data-testid={`${testPrefix}-object-prompts`}>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Per-object guidance (optional)</label>
                        <p className="mb-1.5 text-[11px] text-stone2">Tell the assistant how to talk about each object — e.g. which fields matter or what to say.</p>
                        <div className="space-y-1.5">
                            {[...stdReads, ...customReads].map((obj) => (
                                <div key={obj} className="flex items-center gap-2">
                                    <span className="w-20 shrink-0 truncate text-xs font-semibold text-indigo-700" title={obj}>{obj}</span>
                                    <input data-testid={`${testPrefix}-prompt-${obj}`} value={(c.object_prompts || {})[obj] || ""}
                                        onChange={(e) => onChange({ object_prompts: { ...(c.object_prompts || {}), [obj]: e.target.value } })}
                                        placeholder={`e.g. For ${obj}, mention the status and the latest update date.`}
                                        className="flex-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-ink" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </AiSection>

            {/* 4 · Can CREATE */}
            <AiSection step="4" title="What it can create" subtitle="New records the assistant may create in Salesforce." icon={Plus} testId={`${testPrefix}-sec-create`}>
                {[["can_create_case", "Case", "Log a support case"], ["can_create_event", "Event", "Book an appointment"], ["can_create_task", "Task", "Log a callback / note"]].map(([key, label, desc]) => (
                    <label key={key} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2">
                        <span className="text-xs text-ink"><b>{label}</b> · <span className="text-stone2">{desc}</span></span>
                        <AiToggle testId={`${testPrefix}-create-${label.toLowerCase()}`} checked={c[key] !== false} onChange={(v) => onChange({ [key]: v, enable_actions: true })} />
                    </label>
                ))}
                <p className="text-[11px] text-stone2">The assistant emails a confirmation when the Contact has an email on file. It can never create custom-object records.</p>
            </AiSection>

            {/* 5 · Escalation */}
            <AiSection step="5" title="Escalation" subtitle="When to hand the caller to a human agent." icon={Users} testId={`${testPrefix}-sec-escalate`}>
                <label className="flex cursor-pointer items-center justify-between gap-3">
                    <span className="text-xs font-medium text-ink">Hand off to a human after ~2 confused turns (or on request / complaint)</span>
                    <AiToggle testId={`${testPrefix}-escalate`} checked={c.escalate_on_confused} onChange={(v) => onChange({ escalate_on_confused: v })} />
                </label>
            </AiSection>
        </div>
    );
}

function AiModelCard({ locked }) {
    const [cfg, setCfg] = useState(null);
    const [apiKey, setApiKey] = useState("");
    const [sttKey, setSttKey] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (locked) return;
        api.get("/settings/ai").then(({ data }) => {
            setCfg(data); setApiKey(data.api_key || ""); setSttKey(data.stt_api_key || "");
        }).catch(() => {});
    }, [locked]);
    if (locked) {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="ai-model-locked">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700"><LockKey size={14} weight="fill" /> AI Voice Assistant · Professional</div>
                <p className="mt-1 text-xs text-stone2">The AI voice assistant (auto-answer, Salesforce lookup, and smart escalation) is available on the <b>Professional</b> plan. Upgrade to route menu options to the AI assistant.</p>
                <a href="/billing" data-testid="ai-model-upgrade" className="mt-3 inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover">Upgrade to Professional</a>
            </div>
        );
    }
    if (!cfg) return null;
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/settings/ai", {
                provider: cfg.provider, model: cfg.model, api_key: apiKey,
                stt_provider: cfg.stt_provider, stt_model: cfg.stt_model, stt_api_key: sttKey,
                auto_analyze: cfg.auto_analyze !== false,
            });
            setCfg(data); setApiKey(data.api_key || ""); setSttKey(data.stt_api_key || "");
            toast.success("AI settings saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };
    const models = {
        openai: ["gpt-5.6", "gpt-5.5", "gpt-4o", "gpt-4o-mini"],
        anthropic: ["claude-fable-5", "claude-sonnet-4-6", "claude-haiku-4-5"],
        gemini: ["gemini-3.6-flash", "gemini-3.1-pro"],
    };
    const sttModels = {
        whisper: ["whisper-1", "gpt-4o-transcribe", "gpt-4o-mini-transcribe"],
        deepgram: ["nova-3", "nova-2"],
        assemblyai: ["best", "nano"],
    };
    const sttLabels = { whisper: "OpenAI Whisper", deepgram: "Deepgram", assemblyai: "AssemblyAI" };
    return (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="ai-model-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-700"><Robot size={14} weight="fill" /> Text AI (assistant &amp; summaries)</div>
            <p className="mt-1 text-xs text-stone2">Bring your own provider key. Powers AI voice-assistant replies and call summaries. Your key is stored encrypted and used only for your workspace.</p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Provider</label>
                    <select data-testid="ai-provider" value={cfg.provider} onChange={(e) => setCfg({ ...cfg, provider: e.target.value, model: models[e.target.value][0] })} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="gemini">Gemini</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Model</label>
                    <select data-testid="ai-model" value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                        {(models[cfg.provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Your API key</label>
                    <input data-testid="ai-api-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={cfg.has_own_key ? "•••••••• (saved)" : "Paste your provider API key"} className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-700"><Robot size={14} weight="fill" /> Call transcription</div>
            <p className="mt-1 text-xs text-stone2">Bring your own speech-to-text key. Powers call-recording transcripts in Call History &amp; Reports.</p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Provider</label>
                    <select data-testid="stt-provider" value={cfg.stt_provider} onChange={(e) => setCfg({ ...cfg, stt_provider: e.target.value, stt_model: sttModels[e.target.value][0] })} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                        {Object.keys(sttLabels).map((p) => <option key={p} value={p}>{sttLabels[p]}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Model</label>
                    <select data-testid="stt-model" value={cfg.stt_model} onChange={(e) => setCfg({ ...cfg, stt_model: e.target.value })} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                        {(sttModels[cfg.stt_provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Your API key</label>
                    <input data-testid="stt-api-key" type="password" value={sttKey} onChange={(e) => setSttKey(e.target.value)} placeholder={cfg.stt_has_own_key ? "•••••••• (saved)" : "Paste your speech-to-text API key"} className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-indigo-200 bg-white/70 p-3" data-testid="ai-auto-analyze-row">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-700">Automated AI Summary &amp; Sentiment</div>
                    <p className="mt-1 text-xs text-stone2">
                        When on, every completed recorded call is automatically transcribed and summarized in the background (uses your API credits).
                        Turn off to save credits — you can still generate a summary manually per call from Call History. Salesforce logging keeps working either way.
                    </p>
                </div>
                <Switch
                    data-testid="ai-auto-analyze-toggle"
                    checked={cfg.auto_analyze !== false}
                    onCheckedChange={(v) => setCfg({ ...cfg, auto_analyze: v })}
                />
            </div>

            <div className="mt-4">
                <button data-testid="ai-model-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50">
                    <FloppyDisk size={15} weight="fill" /> {saving ? "Saving…" : "Save AI settings"}
                </button>
            </div>
        </div>
    );
}

function AiNeedsSalesforce({ testPrefix }) {
    return (
        <div data-testid={`${testPrefix}-needs-sf`} className="mt-3 rounded-xl border border-sky-200 bg-sky-50/70 p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-sky-700">
                <Robot size={14} weight="fill" /> AI voice assistant · Needs Salesforce
            </div>
            <p className="mt-1 text-xs text-stone2">The AI assistant reads and creates records in Salesforce and verifies callers against it, so it needs an active Salesforce connection. Connect Salesforce to configure it — until then, callers choosing this option will be routed to your agents.</p>
            <a href="/settings?tab=salesforce" data-testid={`${testPrefix}-connect-sf`} className="mt-3 inline-flex rounded-full bg-[#00A1E0] px-4 py-2 text-xs font-semibold text-white transition-all hover:-translate-y-0.5">Connect Salesforce</a>
        </div>
    );
}

function AiLockedNotice({ testPrefix }) {
    return (
        <div data-testid={`${testPrefix}-locked`} className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700">
                <Robot size={14} weight="fill" /> AI voice assistant · Professional
            </div>
            <p className="mt-1 text-xs text-stone2">This option is set to the AI voice assistant, which is available on the <b>Professional</b> plan. Upgrade to configure and use it — until then, callers choosing this option will be routed to your agents.</p>
            <a href="/billing" data-testid={`${testPrefix}-upgrade`} className="mt-3 inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover">Upgrade to Professional</a>
        </div>
    );
}

export default function CallQueues() {
    const { user } = useAuth();
    const aiLocked = !(user?.features || {}).ai_assistant;
    const [sfConnected, setSfConnected] = useState(true);
    const [ivrEnabled, setIvrEnabled] = useState(false);
    const [greeting, setGreeting] = useState("");
    const [queues, setQueues] = useState([]);
    const [agents, setAgents] = useState([]);
    const [holdEnabled, setHoldEnabled] = useState(false);
    const [maxWait, setMaxWait] = useState(300);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const normalize = (list) => (list || []).map((q) => ({
        script: "", submenu_enabled: false, submenu_greeting: "", children: [], route_type: "agents", ai_config: null, ...q,
        children: (q.children || []).map((c) => ({ script: "", route_type: "agents", ai_config: null, ...c })),
    }));

    const load = async () => {
        try {
            const { data } = await api.get("/settings/queues");
            setIvrEnabled(data.ivr_enabled);
            setGreeting(data.ivr_greeting || "");
            setQueues(normalize(data.queues));
            setAgents(data.agents || []);
            setHoldEnabled(data.queue_hold_enabled || false);
            setMaxWait(data.max_wait_seconds || 300);
            try {
                const { data: sf } = await api.get("/integrations/salesforce");
                setSfConnected(!!sf.connected);
            } catch (_e) { setSfConnected(false); }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const nextDigit = (used) => DIGITS.slice(1).concat("0").find((d) => !used.includes(d)) || "";

    const addQueue = () => {
        const digit = nextDigit(queues.map((q) => q.digit));
        setQueues([...queues, { name: "", digit, agent_ids: [], script: "", submenu_enabled: false, submenu_greeting: "", children: [], route_type: "agents", ai_config: null }]);
    };
    const updateQueue = (i, patch) => setQueues(queues.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
    const removeQueue = (i) => setQueues(queues.filter((_, idx) => idx !== i));
    const toggleAgent = (i, agentId) => {
        const q = queues[i];
        const has = q.agent_ids.includes(agentId);
        updateQueue(i, { agent_ids: has ? q.agent_ids.filter((a) => a !== agentId) : [...q.agent_ids, agentId] });
    };
    const setRoute = (i, rt) => {
        const q = queues[i];
        const patch = { route_type: rt, submenu_enabled: rt === "submenu" };
        if (rt === "submenu" && (q.children || []).length === 0) patch.children = [{ name: "", digit: "1", agent_ids: [], script: "", route_type: "agents", ai_config: null }];
        if (rt === "ai_assistant" && !q.ai_config) patch.ai_config = { ...DEFAULT_AI };
        updateQueue(i, patch);
    };
    const updateAi = (i, patch) => updateQueue(i, { ai_config: { ...(queues[i].ai_config || DEFAULT_AI), ...patch } });

    // ---- sub-option (child) helpers ----
    const addChild = (i) => {
        const q = queues[i];
        const digit = nextDigit((q.children || []).map((c) => c.digit));
        updateQueue(i, { children: [...(q.children || []), { name: "", digit, agent_ids: [], script: "", route_type: "agents", ai_config: null }] });
    };
    const updateChild = (i, ci, patch) => {
        const q = queues[i];
        updateQueue(i, { children: q.children.map((c, idx) => (idx === ci ? { ...c, ...patch } : c)) });
    };
    const removeChild = (i, ci) => updateQueue(i, { children: queues[i].children.filter((_, idx) => idx !== ci) });
    const toggleChildAgent = (i, ci, agentId) => {
        const c = queues[i].children[ci];
        const has = c.agent_ids.includes(agentId);
        updateChild(i, ci, { agent_ids: has ? c.agent_ids.filter((a) => a !== agentId) : [...c.agent_ids, agentId] });
    };
    const setChildRoute = (i, ci, rt) => {
        const c = queues[i].children[ci];
        const patch = { route_type: rt };
        if (rt === "ai_assistant" && !c.ai_config) patch.ai_config = { ...DEFAULT_AI };
        updateChild(i, ci, patch);
    };
    const updateChildAi = (i, ci, patch) => updateChild(i, ci, { ai_config: { ...(queues[i].children[ci].ai_config || DEFAULT_AI), ...patch } });

    const save = async () => {
        if (queues.some((q) => !q.name.trim())) { toast.error("Every queue needs a name"); return; }
        const topDigits = queues.map((q) => q.digit);
        if (new Set(topDigits).size !== topDigits.length) { toast.error("Two queues share the same digit — each department needs a unique key"); return; }
        for (const q of queues) {
            if (q.route_type === "submenu") {
                if (!(q.children || []).length) { toast.error(`"${q.name || "Queue"}" has a sub-menu on but no sub-options`); return; }
                if (q.children.some((c) => !c.name.trim())) { toast.error(`Every sub-option under "${q.name}" needs a name`); return; }
                const subDigits = q.children.map((c) => c.digit);
                if (new Set(subDigits).size !== subDigits.length) { toast.error(`Two sub-options under "${q.name}" share the same digit`); return; }
            }
        }
        setSaving(true);
        try {
            const { data } = await api.put("/settings/queues", {
                ivr_enabled: ivrEnabled, ivr_greeting: greeting, queues,
                queue_hold_enabled: holdEnabled, max_wait_seconds: Number(maxWait) || 300,
            });
            setQueues(normalize(data.queues));
            toast.success("Call queues saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="rounded-2xl border border-stone-200 bg-white p-8 text-sm text-stone2" data-testid="queues-loading">Loading queues…</div>;

    return (
        <div className="space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8" data-testid="queues-panel">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                        <Queue size={14} weight="fill" /> Inbound menu &amp; queues
                    </div>
                    <p className="mt-1 text-sm text-stone2">Greet callers and route each option to your <strong>agents</strong>, a <strong>sub-menu</strong>, or an <strong>AI voice assistant</strong> that looks up the caller in Salesforce, answers their order/case questions, and hands off to a human when needed.</p>
                </div>
                <label className="flex shrink-0 cursor-pointer items-center gap-2" data-testid="ivr-toggle">
                    <span className="text-xs font-semibold text-stone2">Menu {ivrEnabled ? "on" : "off"}</span>
                    <button type="button" onClick={() => setIvrEnabled(!ivrEnabled)} className={`relative h-6 w-11 rounded-full transition-colors ${ivrEnabled ? "bg-emerald-500" : "bg-stone-300"}`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${ivrEnabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                </label>
            </div>

            <AiModelCard locked={aiLocked} />

            <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone2">Greeting message</label>
                <textarea data-testid="ivr-greeting" value={greeting} onChange={(e) => setGreeting(e.target.value)} rows={2}
                    placeholder="Thank you for calling Inspiro. Please choose from the following options."
                    className="mt-2 w-full resize-none rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink" />
                <p className="mt-1.5 text-[11px] text-stone2">Read aloud (text-to-speech) before the menu options. Each queue adds "For {`{name}`}, press {`{digit}`}."</p>
            </div>

            <div className="space-y-4">
                {queues.length === 0 && (
                    <div className="rounded-xl border border-dashed border-stone-300 p-6 text-center text-sm text-stone2" data-testid="queues-empty">
                        No queues yet. Add one for each department you want to route calls to.
                    </div>
                )}
                {queues.map((q, i) => (
                    <div key={q.id || i} className="rounded-2xl border border-stone-200 bg-stone-50 p-4" data-testid={`queue-card-${i}`}>
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex-1 min-w-[160px]">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Queue name</label>
                                <input data-testid={`queue-name-${i}`} value={q.name} onChange={(e) => updateQueue(i, { name: e.target.value })} placeholder="Sales"
                                    className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                            </div>
                            <div className="w-24">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Press digit</label>
                                <select data-testid={`queue-digit-${i}`} value={q.digit} onChange={(e) => updateQueue(i, { digit: e.target.value })}
                                    className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                                    {DIGITS.map((d) => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <button data-testid={`queue-remove-${i}`} onClick={() => removeQueue(i)}
                                className="grid h-10 w-10 place-items-center rounded-lg border border-red-200 text-red-500 transition-colors hover:bg-red-50">
                                <Trash size={16} />
                            </button>
                        </div>

                        <RouteSelector value={q.route_type} onChange={(rt) => setRoute(i, rt)} allowSubmenu testPrefix={`queue-route-${i}`} aiLocked={aiLocked} />

                        {q.route_type === "agents" && (
                            <>
                                <div className="mt-3">
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Message before connecting (optional)</label>
                                    <input data-testid={`queue-script-${i}`} value={q.script || ""} onChange={(e) => updateQueue(i, { script: e.target.value })}
                                        placeholder="Connecting you to our Sales team. Please hold."
                                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                </div>
                                <div className="mt-3">
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Agents in this queue ({q.agent_ids.length})</label>
                                    <AgentChips agents={agents} selected={q.agent_ids} onToggle={(id) => toggleAgent(i, id)} testPrefix={`queue-agents-${i}`} />
                                </div>
                            </>
                        )}

                        {q.route_type === "ai_assistant" && !aiLocked && sfConnected && (
                            <AiConfigEditor cfg={q.ai_config} onChange={(patch) => updateAi(i, patch)} testPrefix={`queue-ai-${i}`} />
                        )}
                        {q.route_type === "ai_assistant" && aiLocked && (
                            <AiLockedNotice testPrefix={`queue-ai-${i}`} />
                        )}
                        {q.route_type === "ai_assistant" && !aiLocked && !sfConnected && (
                            <AiNeedsSalesforce testPrefix={`queue-ai-${i}`} />
                        )}

                        {q.route_type === "submenu" && (
                            <div className="mt-3 space-y-3">
                                <div>
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Sub-menu greeting</label>
                                    <textarea data-testid={`submenu-greeting-${i}`} value={q.submenu_greeting || ""} onChange={(e) => updateQueue(i, { submenu_greeting: e.target.value })} rows={2}
                                        placeholder="You've reached Sales. For a new purchase press 1, for an existing order press 2."
                                        className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                </div>
                                <div className="space-y-3 rounded-xl border-l-2 border-emerald-300 bg-white p-3">
                                    {(q.children || []).map((c, ci) => (
                                        <div key={c.id || ci} className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid={`subqueue-card-${i}-${ci}`}>
                                            <div className="flex flex-wrap items-end gap-3">
                                                <div className="flex-1 min-w-[140px]">
                                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Sub-option name</label>
                                                    <input data-testid={`subqueue-name-${i}-${ci}`} value={c.name} onChange={(e) => updateChild(i, ci, { name: e.target.value })} placeholder="New Purchase"
                                                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                                </div>
                                                <div className="w-24">
                                                    <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Press digit</label>
                                                    <select data-testid={`subqueue-digit-${i}-${ci}`} value={c.digit} onChange={(e) => updateChild(i, ci, { digit: e.target.value })}
                                                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink">
                                                        {DIGITS.map((d) => <option key={d} value={d}>{d}</option>)}
                                                    </select>
                                                </div>
                                                <button data-testid={`subqueue-remove-${i}-${ci}`} onClick={() => removeChild(i, ci)}
                                                    className="grid h-10 w-10 place-items-center rounded-lg border border-red-200 text-red-500 transition-colors hover:bg-red-50">
                                                    <Trash size={16} />
                                                </button>
                                            </div>

                                            <RouteSelector value={c.route_type} onChange={(rt) => setChildRoute(i, ci, rt)} allowSubmenu={false} testPrefix={`subqueue-route-${i}-${ci}`} aiLocked={aiLocked} />

                                            {c.route_type === "agents" && (
                                                <>
                                                    <div className="mt-2">
                                                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Script for this option (optional)</label>
                                                        <input data-testid={`subqueue-script-${i}-${ci}`} value={c.script || ""} onChange={(e) => updateChild(i, ci, { script: e.target.value })}
                                                            placeholder="Great! Connecting you to a new-sales specialist."
                                                            className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                                    </div>
                                                    <div className="mt-2">
                                                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Agents ({c.agent_ids.length})</label>
                                                        <AgentChips agents={agents} selected={c.agent_ids} onToggle={(id) => toggleChildAgent(i, ci, id)} testPrefix={`subqueue-agents-${i}-${ci}`} />
                                                    </div>
                                                </>
                                            )}
                                            {c.route_type === "ai_assistant" && !aiLocked && sfConnected && (
                                                <AiConfigEditor cfg={c.ai_config} onChange={(patch) => updateChildAi(i, ci, patch)} testPrefix={`subqueue-ai-${i}-${ci}`} />
                                            )}
                                            {c.route_type === "ai_assistant" && aiLocked && (
                                                <AiLockedNotice testPrefix={`subqueue-ai-${i}-${ci}`} />
                                            )}
                                            {c.route_type === "ai_assistant" && !aiLocked && !sfConnected && (
                                                <AiNeedsSalesforce testPrefix={`subqueue-ai-${i}-${ci}`} />
                                            )}
                                        </div>
                                    ))}
                                    <button data-testid={`add-subqueue-${i}`} onClick={() => addChild(i)}
                                        className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-stone-50">
                                        <Plus size={14} weight="bold" /> Add sub-option
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="rounded-2xl border border-stone-200 p-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-sm font-semibold text-ink">Hold callers when agents are busy</div>
                        <p className="mt-0.5 text-xs text-stone2">Instead of hanging up, play hold music and announce their position in line until an agent becomes available.</p>
                    </div>
                    <button type="button" data-testid="hold-toggle" onClick={() => setHoldEnabled(!holdEnabled)}
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${holdEnabled ? "bg-emerald-500" : "bg-stone-300"}`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${holdEnabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                </div>
                {holdEnabled && (
                    <div className="mt-3 flex items-center gap-2">
                        <label className="text-xs font-semibold text-stone2">Max wait</label>
                        <input type="number" min={30} max={1800} data-testid="max-wait-input" value={maxWait} onChange={(e) => setMaxWait(e.target.value)}
                            className="w-24 rounded-lg border border-stone-300 px-3 py-1.5 text-sm outline-none focus:border-ink" />
                        <span className="text-xs text-stone2">seconds, then play a "try again later" message. Hold music uses the URL from the Team/Routing settings.</span>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                <button data-testid="add-queue" onClick={addQueue}
                    className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-stone-50">
                    <Plus size={16} weight="bold" /> Add queue
                </button>
                <button data-testid="save-queues" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50">
                    <FloppyDisk size={16} weight="fill" /> {saving ? "Saving…" : "Save queues"}
                </button>
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-stone-50 p-3 text-[11px] text-stone2">
                <Phone size={14} className="mt-0.5 shrink-0" />
                <span>Callers hear the greeting and pick an option. Each option rings agents, opens a sub-menu, or connects to the AI assistant (which verifies identity, reads Salesforce order/case data aloud, and escalates to an agent when it can't help or the caller asks for a human).</span>
            </div>
        </div>
    );
}
